// TradeDelta (TASK-901) — the trade simulation, which was a refusal.
//
// TASK-868 built the engine and nothing on the canvas could reach it. Three prompts asked for
// it: two were refused honestly ("I can't simulate a trade and show its effect — there's no view
// for it yet") and the third opened `Swaps`, whose own copy says sub-asset-class weights are
// identical before and after, while the reply claimed it brought the slot back to target
// (TASK-899). So this widget converts two clean refusals and one false claim into the epic's
// headline capability.
//
// THE VERDICT IS RENDERED, NEVER RE-DERIVED.
// `breaches_before` / `breaches_after` / `opens_new_breach` are computed server-side by applying
// the trims to a deep copy and re-running the calculator. This file displays them. It does not
// recompute them from `drift_after`, because two derivations of one fact are how the two come to
// disagree — which is the defect this whole epic keeps finding. The one thing computed here is
// the per-slot BEFORE→AFTER transition, and it is computed from the same two snapshots the
// server used, never from prose.
//
// The case that matters: Julian Ammann, 2026-09-01. `breaches_before: ["Foreign (Dev. Markets)"]`
// → `breaches_after: ["Cash & Money Market"]`. One breach before, one after — a naive count
// comparison reads "no worse" and the old assistant stamped "✅ The drift breach would be
// resolved". It cleared one slot and opened another.
import { AlertTriangle, ArrowRight, Check, Loader2 } from 'lucide-react';

import type { RebalanceProposal, SlotMetric, TrimRow } from '../../../services/rebalanceService';
import { useRebalanceProposal, useSaveProposal } from '../../../hooks/useRebalanceProposal';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

const chf = (v: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency', currency: 'CHF', maximumFractionDigits: 0,
  }).format(v);

const pct = (v: number) => `${v.toFixed(1)}%`;
/** Percentage POINTS, not a percentage. Signed, because the direction is the point. */
const pp = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`;

export type SlotTransition = 'cleared' | 'opened' | 'still-breached' | 'unchanged';

export interface SlotRow {
  slot: string;
  before: SlotMetric;
  after: SlotMetric | null;
  transition: SlotTransition;
}

/** Every slot, with what the trade does to it.
 *
 *  Exported and pure so the verdict strip, the table and the tests all read one function. A slot
 *  present before and absent after is not "cleared" — it is missing data, and `after: null`
 *  renders as unknown rather than as success. */
export function slotRows(proposal: RebalanceProposal): SlotRow[] {
  const before = proposal.drift_before?.by_slot ?? {};
  const after = proposal.drift_after?.by_slot ?? {};
  return Object.keys(before)
    .map((slot) => {
      const b = before[slot];
      const a = after[slot] ?? null;
      let transition: SlotTransition = 'unchanged';
      if (a !== null) {
        if (b.breach && !a.breach) transition = 'cleared';
        else if (!b.breach && a.breach) transition = 'opened';
        else if (b.breach && a.breach) transition = 'still-breached';
      }
      return { slot, before: b, after: a, transition };
    })
    .sort((x, y) => {
      // The slots that changed state first — that is what the advisor is deciding on.
      const rank = (t: SlotTransition) =>
        t === 'opened' ? 0 : t === 'cleared' ? 1 : t === 'still-breached' ? 2 : 3;
      const d = rank(x.transition) - rank(y.transition);
      return d !== 0 ? d : Math.abs(y.before.drift) - Math.abs(x.before.drift);
    });
}

function VerdictStrip({ proposal }: { proposal: RebalanceProposal }) {
  const changed = slotRows(proposal).filter((r) => r.transition !== 'unchanged');
  const opensNew = proposal.opens_new_breach;

  return (
    <div
      data-testid="trade-delta-verdict"
      className={`mb-3 rounded-md border px-3 py-2 ${
        opensNew ? 'border-amber-500/60 bg-amber-500/5' : 'border-border bg-muted/30'
      }`}
    >
      <div className="flex items-center gap-2 text-[12px] font-medium text-foreground">
        <span data-testid="breaches-before">
          breaches before {proposal.breaches_before?.length ?? 0}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span data-testid="breaches-after">
          breaches after {proposal.breaches_after?.length ?? 0}
        </span>
      </div>

      {changed.map((row) => (
        <div key={row.slot} className="mt-1 flex items-center gap-2 text-[11px]">
          <span className="max-w-[45%] truncate text-muted-foreground">{row.slot}</span>
          <span
            data-testid={`slot-transition-${row.slot}`}
            className={
              row.transition === 'opened'
                ? 'font-semibold text-amber-600'
                : row.transition === 'cleared'
                  ? 'font-semibold text-emerald-600'
                  : 'font-semibold text-destructive'
            }
          >
            {row.transition === 'opened'
              ? 'CLEARED → BREACH'
              : row.transition === 'cleared'
                ? 'BREACH → CLEARED'
                : 'BREACH → BREACH'}
          </span>
        </div>
      ))}

      {/* The sentence the old assistant did not say. A trade that clears one slot and breaches
          another must not read as success, and the count alone does not show it: Ammann goes
          1 → 1. */}
      {opensNew && (
        <p
          data-testid="opens-new-breach-warning"
          className="mt-1.5 flex items-start gap-1.5 text-[11px] font-medium text-amber-700"
        >
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            This trade opens a new breach in{' '}
            {(proposal.new_breaches ?? []).join(', ') || 'another slot'}. It does not leave the
            portfolio in compliance.
          </span>
        </p>
      )}
      {!opensNew && proposal.resolves_target_slot && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-700">
          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Brings {proposal.slot} back inside its band and opens no new breach.</span>
        </p>
      )}
    </div>
  );
}

function SlotTable({ rows, tolerance }: { rows: SlotRow[]; tolerance: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="trade-delta-slots">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="py-1 text-left font-medium">Sub-asset class</th>
            <th className="py-1 text-right font-medium">Before</th>
            <th className="py-1 text-right font-medium">After</th>
            <th className="py-1 text-right font-medium">Target</th>
            {/* Points, not percent — the header says so, per DriftBars. */}
            <th className="py-1 text-right font-medium">Δpp</th>
            <th className="py-1 text-right font-medium">Band</th>
            <th className="py-1 text-right font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.slot} className="border-b border-border/40 last:border-0">
              <td className="max-w-[180px] truncate py-1 text-foreground">{r.slot}</td>
              <td className="py-1 text-right tabular-nums">{pct(r.before.current)}</td>
              <td className="py-1 text-right tabular-nums">
                {r.after ? pct(r.after.current) : '—'}
              </td>
              <td className="py-1 text-right tabular-nums text-muted-foreground">
                {pct(r.before.target)}
              </td>
              <td className="py-1 text-right tabular-nums">
                {r.after ? pp(r.after.current - r.before.current) : '—'}
              </td>
              <td className="py-1 text-right tabular-nums text-muted-foreground">
                ±{tolerance.toFixed(1)}
              </td>
              <td className="py-1 text-right">
                {r.after === null ? (
                  <span className="text-muted-foreground">unknown</span>
                ) : r.after.breach ? (
                  <span className="font-medium text-destructive">breach</span>
                ) : (
                  <span className="text-emerald-600">in band</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TradeRows({ rows, sizingUnit }: { rows: TrimRow[]; sizingUnit: string }) {
  const sorted = [...rows].sort((a, b) => (b.trim_chf ?? 0) - (a.trim_chf ?? 0));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="trade-delta-trades">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="py-1 text-left font-medium">#</th>
            <th className="py-1 text-left font-medium">Security</th>
            <th className="py-1 text-left font-medium">ISIN</th>
            <th className="py-1 text-left font-medium">Slot</th>
            <th className="py-1 text-right font-medium">Current {sizingUnit}</th>
            <th className="py-1 text-right font-medium">Trim {sizingUnit}</th>
            <th className="py-1 text-right font-medium">Post {sizingUnit}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            // TASK-876: keyed by position_id so "check row 12" lands on the same row for
            // whoever it is sent to. The visible index is 1-based for the same reason.
            <tr key={r.position_id} data-position-id={r.position_id}
                className="border-b border-border/40 last:border-0">
              <td className="py-1 text-muted-foreground tabular-nums">{i + 1}</td>
              <td className="max-w-[200px] truncate py-1 text-foreground">
                {r.security_name ?? '—'}
              </td>
              <td className="py-1 font-mono text-[10px] text-muted-foreground">{r.isin ?? '—'}</td>
              <td className="max-w-[140px] truncate py-1 text-muted-foreground">{r.slot}</td>
              <td className="py-1 text-right tabular-nums">{chf(r.current_value_chf)}</td>
              <td className="py-1 text-right tabular-nums font-medium">{chf(r.trim_chf)}</td>
              <td className="py-1 text-right tabular-nums">{chf(r.post_value_chf)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface TradeDeltaProps {
  clientId: string;
  clientName?: string;
  proposal?: RebalanceProposal | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: { message?: string } | null;
  onSave?: (proposal: RebalanceProposal) => void;
  isSaving?: boolean;
  savedAt?: string | null;
}

export function TradeDelta({
  clientId, clientName, proposal, isLoading, isError, error, onSave, isSaving, savedAt,
}: TradeDeltaProps) {
  return (
    <WidgetContainer source="Rebalancing proposal">
      {isLoading && (
        <div className="flex items-center gap-2 py-6 text-[12px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Sizing the trade…
        </div>
      )}

      {isError && (
        <p className="py-6 text-[12px] text-destructive" data-testid="trade-delta-error">
          {/* Says which client and what failed. A silent empty panel is the defect TASK-898
              is about, one surface along. */}
          Couldn’t build a proposal{clientName ? ` for ${clientName}` : ''}
          {error?.message ? `: ${error.message}` : '.'}
        </p>
      )}

      {!isLoading && !isError && !proposal && (
        <p className="py-6 text-[12px] text-muted-foreground">
          No proposal to show — nothing is outside its band.
        </p>
      )}

      {!isLoading && !isError && proposal && (
        <>
          <header className="mb-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h3 className="text-[13px] font-semibold text-foreground">
                {clientName ?? 'Client'} · {proposal.slot}
              </h3>
              {/* Non-negotiable, and first: this widget sizes a trade, it does not place one. */}
              <span
                data-testid="no-orders-notice"
                className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
              >
                Recommendation only — no orders are placed
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Trim {chf(proposal.trim_chf)} of {chf(proposal.slot_value_chf)} ·{' '}
              {proposal.rows.length} position{proposal.rows.length === 1 ? '' : 's'} ·{' '}
              as at {new Date(proposal.timestamp).toLocaleString('de-CH')}
            </p>
            {proposal.shortfall_chf > 0 && (
              <p className="mt-0.5 text-[11px] font-medium text-amber-700"
                 data-testid="shortfall-notice">
                The holdings in this slot fall {chf(proposal.shortfall_chf)} short of the trim
                required — this proposal does not reach target on its own.
              </p>
            )}
          </header>

          <VerdictStrip proposal={proposal} />

          {/* TASK-870: sizing is CHF, and the widget says WHY once rather than putting a
              share count it does not have next to each row. */}
          {!proposal.share_sizing_available && (
            <p
              data-testid="chf-not-shares"
              className="mb-2 text-[11px] text-muted-foreground"
            >
              Sized in <span className="font-medium">CHF, not shares</span> — no share price is
              available for these positions, and a quantity would be invented.
            </p>
          )}

          <SlotTable rows={slotRows(proposal)} tolerance={proposal.tolerance_pct} />

          <h4 className="mb-1 mt-3 text-[12px] font-semibold text-foreground">
            Trades ({proposal.rows.length})
          </h4>
          <TradeRows rows={proposal.rows} sizingUnit={proposal.sizing_unit || 'CHF'} />

          <footer className="mt-3 flex flex-wrap items-center gap-2">
            <OpenAsSpreadsheet
              sourceWidget="TradeDelta"
              clientId={clientId}
              asOf={proposal.timestamp ?? null}
              build={() => ({
                title: `Rebalancing proposal — ${proposal.slot}`,
                sheetName: 'Trades',
                columns: [
                  { header: 'Row', width: 6 },
                  { header: 'Security', width: 32 },
                  { header: 'ISIN', width: 16 },
                  { header: 'Slot', width: 24 },
                  { header: 'Current CHF', fmt: 'currency', width: 16 },
                  { header: 'Trim CHF', fmt: 'currency', width: 16 },
                  { header: 'Post CHF', fmt: 'currency', width: 16 },
                  // Carried so the sheet can be tied back to a row on screen.
                  { header: 'position_id', width: 38 },
                ],
                rows: [...proposal.rows]
                  .sort((a, b) => (b.trim_chf ?? 0) - (a.trim_chf ?? 0))
                  .map((r, i) => [
                    i + 1,
                    r.security_name ?? '',
                    r.isin ?? '',
                    r.slot,
                    r.current_value_chf,
                    r.trim_chf,
                    r.post_value_chf,
                    r.position_id,
                  ]),
              })}
            />
            {onSave && (
              <button
                type="button"
                data-testid="save-proposal"
                disabled={isSaving}
                onClick={() => onSave(proposal)}
                className="rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Save proposal'}
              </button>
            )}
            {savedAt && (
              <span className="text-[11px] text-emerald-700" data-testid="saved-notice">
                Saved {new Date(savedAt).toLocaleString('de-CH')}
              </span>
            )}
          </footer>
        </>
      )}
    </WidgetContainer>
  );
}


/** Registry entry point: client-scoped, receives `{ clientId }` and fetches for itself.
 *
 *  Split from the presentational component above so the verdict logic can be tested against a
 *  fixture without a query client — the tests that matter here are about what the widget SAYS
 *  when a trade opens a new breach, not about data plumbing. */
export function TradeDeltaPanel({
  clientId, clientName, slot,
}: { clientId: string; clientName?: string; slot?: string }) {
  const proposal = useRebalanceProposal(clientId, slot);
  const save = useSaveProposal(clientId);

  return (
    <TradeDelta
      clientId={clientId}
      clientName={clientName}
      proposal={proposal.data ?? null}
      isLoading={proposal.isLoading}
      isError={proposal.isError}
      error={proposal.error as { message?: string } | null}
      onSave={(p) => save.mutate(p)}
      isSaving={save.isPending}
      savedAt={save.isSuccess ? new Date().toISOString() : null}
    />
  );
}

export default TradeDeltaPanel;
